import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline, Stack } from "PFComponents/containers";
import { Label } from "PFComponents/label";
import { LoadingDots } from "PFComponents/loading_dots";
import { Slider } from "PFComponents/slider";
import { Typography } from "PFComponents/typography";
import { ApiNumericRange, NumericRange } from "PFTypes";
import { useTranslation } from "react-i18next";

import css from "./filter_range_slider.module.scss";

type FilterRangeSliderProps = {
  label?: string;
  required?: boolean;
  value: ApiNumericRange;
  onChange: (updatedRange: ApiNumericRange) => void;
  onClear?: () => void;
  onRestore?: () => void;
  disabled?: boolean;
  loading?: boolean;
} & NumericRange;

export const FilterRangeSlider = ({
  min,
  max,
  label,
  required,
  onChange,
  value,
  onClear,
  onRestore,
  disabled,
  loading
}: FilterRangeSliderProps) => {
  const { t } = useTranslation("filters", { keyPrefix: "number" });

  const isRangeValid = !isNaN(min) && !isNaN(max) && min < max;
  const isValueDefined = value && !isNaN(value?.gte) && !isNaN(value?.lte);
  const formattedValue = value.gte === value.lte ? value.gte : `${value.gte} - ${value.lte}`;

  return (
    <Stack>
      <Inline justifyContent="space-between">
        {label && <Label text={label} required={required} />}
        <Inline>
          {onClear && <ActionIcon name="filter-clean" size="sm" onClick={onClear} color="paletteBlue0" />}
          {onRestore && <ActionIcon name="history" size="sm" onClick={onRestore} color="paletteBlue0" />}
        </Inline>
      </Inline>

      {loading ? (
        <LoadingDots circleSize="xs" circlesEnabled />
      ) : isRangeValid ? (
        <Slider
          multiRange
          value={{
            min: Math.max(value?.gte ?? min, min),
            max: Math.min(value?.lte ?? max, max)
          }}
          min={min}
          max={max}
          onChange={({ min, max }) => onChange({ gte: min, lte: max })}
          disabled={disabled}
          showSteps={false}
          showMinMax={false}
          tip={t("minMax", { min, max })}
          small
        />
      ) : (
        <Typography variant="bodyBold" className={classNames({ [css.disabledText]: disabled })}>
          {isValueDefined ? formattedValue : t("noDataAvailable")}
        </Typography>
      )}
    </Stack>
  );
};
